import {
    ChangeEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import {
    Button,
    CircularProgress,
    createStyles,
    Grid,
    Input,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import NumberFormat from "react-number-format";
import { formatAuctionDateTime } from "../../../helpers/auctionDateTime";
import { IParcelBidLog } from "../../../models/parcel/IParcelBidLog";
import { LoadState } from "../../../models/utility/LoadState";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import LoaderButton from "../LoaderButton";
import CountdownTimer from "../timers/CountdownTimer";
import ExportBidLogCSVButton from "./ExportBidLogCSVButton";

type ParcelBidLogListProps = WithStyles<typeof styles> & {
    auctionClosed: boolean;
    auctionEndDate: string;
    parcelId: string;
    parcelName: string;
    auctionName: string;
    startingBid: number;
    lastAcceptedBidOverride?: number;
    onUpdateClicked: (value: number | null) => void;
    isSaving: boolean;
};

const BlackCountdownTimer = withStyles({
    digit: {
        color: "#000"
    },
    digitCaption: {
        color: "#000"
    },
    digitColon: {
        color: "#000"
    }
})(CountdownTimer);

const styles = (theme: Theme) =>
    createStyles({
        countdownWrapper: {
            display: "inline-block",
            paddingLeft: theme.spacing(),
            width: 150
        },
        currentBid: { color: theme.palette.success.main },
        exportButtonWrapper: {
            alignItems: "center",
            display: "flex"
        },
        finalBid: {
            color: theme.palette.primary.main,
            maxWidth: theme.spacing(16),
            marginRight: theme.spacing()
        },
        button: { marginLeft: theme.spacing() },
        loaderWrapper: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            margin: theme.spacing(5),
            width: "100%"
        },
        table: { width: "100%" },
        tableCell: { borderColor: theme.palette.divider },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        tableLast: { marginBottom: 0 },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            borderColor: theme.palette.grey[500]
        },
        tableRowDeclined: {
            backgroundColor: `${theme.palette.primary.light} !important`,
            borderColor: `${theme.palette.primary.main} !important`
        },
        tableRowHighest: {
            backgroundColor: `${theme.palette.success.light} !important`,
            borderColor: `${theme.palette.success.main} !important`
        },
        timeLeft: {
            display: "inline-block",
            marginTop: theme.spacing()
        }
    });

function ParcelBidLogList(props: ParcelBidLogListProps) {
    const {
        auctionClosed,
        auctionEndDate,
        auctionName,
        classes,
        isSaving,
        lastAcceptedBidOverride,
        onUpdateClicked,
        parcelId,
        parcelName,
        startingBid
    } = props;
    const dispatch = useAppDispatch();
    const parcelAdminBidLogs = useAppSelector(
        (state) => state.parcelBidStoreState.parcelAdminBidLogs
    );

    const parcelLogState = parcelAdminBidLogs.find(
        (e) => e.parcelId === parcelId
    );

    const getInitialOverrideBidValue = useCallback(() => {
        return (
            lastAcceptedBidOverride ??
            parcelAdminBidLogs
                .find((e) => e.parcelId === parcelId)
                ?.logs.find((e) => e.isAccepted)?.bidAmount ??
            startingBid
        );
    }, [lastAcceptedBidOverride, parcelAdminBidLogs, parcelId, startingBid]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isOverridingFinalBid, setIsOverridingFinalBid] = useState(false);
    const [overrideFinalBidValue, setOverrideFinalBidValue] = useState(
        getInitialOverrideBidValue
    );

    const initialLoadTriggered = useRef(false);

    useEffect(() => {
        if (initialLoadTriggered.current) {
            return;
        }

        if (
            !parcelLogState ||
            parcelLogState.loadState === LoadState.NotLoaded
        ) {
            dispatch(
                ParcelBidStore.actionCreators.loadParcelAdminBidLogs(parcelId)
            );
        }

        initialLoadTriggered.current = true;

        return () => {
            initialLoadTriggered.current = false;
        };
    }, [dispatch, parcelId, parcelAdminBidLogs]);

    useEffect(() => {
        return () => {
            dispatch(
                ParcelBidStore.actionCreators.toggleParcelAdminBidLogLoadState(
                    parcelId,
                    LoadState.NotLoaded
                )
            );
        };
    }, [dispatch, parcelId]);

    useEffect(() => {
        if (!isSaving) {
            setIsOverridingFinalBid(false);
        }
    }, [isSaving]);

    useEffect(() => {
        if (!isOverridingFinalBid) {
            setOverrideFinalBidValue(getInitialOverrideBidValue());
        }
    }, [getInitialOverrideBidValue, isOverridingFinalBid]);

    const renderBidRow = useCallback(
        (bid: IParcelBidLog, topBidId: string) => {
            let rowClasses = classes.tableRow;

            if (!bid.isAccepted) {
                rowClasses += ` ${classes.tableRowDeclined}`;
            } else if (auctionClosed && bid.parcelBidId === topBidId) {
                rowClasses += ` ${classes.tableRowHighest}`;
            }

            return (
                <TableRow key={bid.parcelBidId} className={rowClasses}>
                    <TableCell className={classes.tableCell} size="small">
                        {
                            formatAuctionDateTime(bid.bidReceivedTime)
                                ?.mediumDateTimeWithSeconds
                        }
                    </TableCell>
                    <TableCell className={classes.tableCell} size="small">
                        <NumberFormat
                            value={bid.bidAmount}
                            thousandSeparator={true}
                            prefix="$"
                            decimalScale={0}
                            fixedDecimalScale={true}
                            displayType="text"
                        />
                    </TableCell>
                    <TableCell className={classes.tableCell} size="small">
                        {bid.resolutionState}
                    </TableCell>
                    <TableCell className={classes.tableCell} size="small">
                        {bid.fullName}
                    </TableCell>
                    <TableCell className={classes.tableCell} size="small">
                        {bid.email}
                    </TableCell>
                    <TableCell className={classes.tableCell} size="small">
                        {bid.phoneNumber}
                    </TableCell>
                </TableRow>
            );
        },
        [auctionClosed, classes]
    );

    const handleChangePage = useCallback(
        (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
            setPage(newPage);
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
        },
        []
    );

    const onOverrideClicked = useCallback(() => {
        if (!isOverridingFinalBid) {
            const shouldOverride = window.confirm(
                "Are you sure you want to override the final bid price? This should only be done if the final sale price was different from the final bid amount."
            );
            setIsOverridingFinalBid(shouldOverride);
        } else {
            onUpdateClicked(overrideFinalBidValue);
        }
    }, [isOverridingFinalBid, onUpdateClicked, overrideFinalBidValue]);

    const onClearOverrideClicked = useCallback(() => {
        const shouldClear = window.confirm(
            "Are you sure you want to clear the final sale price? The displayed price will return to the value of the last accepted bid."
        );
        if (shouldClear) {
            onUpdateClicked(null);
        }
    }, [onUpdateClicked]);

    if (!parcelLogState || parcelLogState.loadState !== LoadState.Loaded) {
        return (
            <div className={classes.loaderWrapper}>
                <CircularProgress color="secondary" />
            </div>
        );
    }

    let currentBid = startingBid;

    if (isOverridingFinalBid) {
        currentBid = overrideFinalBidValue;
    } else if (lastAcceptedBidOverride) {
        currentBid = lastAcceptedBidOverride;
    } else if (parcelLogState.logs && parcelLogState.logs.length > 0) {
        const firstAccepted = parcelLogState.logs.find((e) => e.isAccepted);
        if (firstAccepted) {
            currentBid = firstAccepted.bidAmount;
        }
    }

    const topBidId =
        parcelLogState.logs.filter((e) => e.isAccepted).length > 0
            ? parcelLogState.logs[0].parcelBidId
            : "";

    return (
        <div>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            className={classes.tableCellHeader}
                            colSpan={lastAcceptedBidOverride ? 2 : 3}
                        >
                            <Typography
                                className={classes.tableHeader}
                                variant="subtitle1"
                            >
                                Parcel Bid History
                            </Typography>
                        </TableCell>
                        {!auctionClosed && (
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={2}
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    <span className={classes.timeLeft}>
                                        Time Left:&nbsp;
                                    </span>
                                    <div className={classes.countdownWrapper}>
                                        <BlackCountdownTimer
                                            entityId={parcelId}
                                            date={auctionEndDate}
                                            hideDayHourZeroes={true}
                                            useCircleLoader={false}
                                        />
                                    </div>
                                </Typography>
                            </TableCell>
                        )}
                        <TableCell
                            className={classes.tableCellHeader}
                            colSpan={auctionClosed ? 4 : 2}
                        >
                            <Typography
                                className={classes.tableHeader}
                                variant="subtitle1"
                            >
                                {auctionClosed
                                    ? lastAcceptedBidOverride !== undefined ||
                                      isOverridingFinalBid
                                        ? "Final Sale Price"
                                        : "Final Bid"
                                    : "Current Bid"}
                                :&nbsp;
                                <NumberFormat
                                    className={
                                        auctionClosed
                                            ? classes.finalBid
                                            : classes.currentBid
                                    }
                                    value={currentBid}
                                    onValueChange={(values) =>
                                        setOverrideFinalBidValue(
                                            values.floatValue ?? 0
                                        )
                                    }
                                    allowNegative={false}
                                    customInput={(props) => {
                                        return (
                                            <Input
                                                classes={{
                                                    input: classes.finalBid
                                                }}
                                                color="primary"
                                                autoFocus={true}
                                                {...props}
                                            />
                                        );
                                    }}
                                    displayType={
                                        isOverridingFinalBid ? "input" : "text"
                                    }
                                    thousandSeparator={true}
                                    prefix="$"
                                    decimalScale={0}
                                    fixedDecimalScale={true}
                                    allowLeadingZeros={false}
                                />
                                {auctionClosed && (
                                    <LoaderButton
                                        loading={isSaving}
                                        variant="contained"
                                        onClick={onOverrideClicked}
                                        className={classes.button}
                                    >
                                        {isOverridingFinalBid
                                            ? "Update"
                                            : "Override Final Bid"}
                                    </LoaderButton>
                                )}
                                {auctionClosed && lastAcceptedBidOverride && (
                                    <LoaderButton
                                        loading={isSaving}
                                        variant="contained"
                                        color="secondary"
                                        onClick={onClearOverrideClicked}
                                        className={classes.button}
                                    >
                                        Clear Override
                                    </LoaderButton>
                                )}
                                {isOverridingFinalBid && (
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            setIsOverridingFinalBid(false)
                                        }
                                        color="secondary"
                                        className={classes.button}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.tableCell} size="small">
                            Received On
                        </TableCell>
                        <TableCell className={classes.tableCell} size="small">
                            Bid Amount
                        </TableCell>
                        <TableCell className={classes.tableCell} size="small">
                            State
                        </TableCell>
                        <TableCell className={classes.tableCell} size="small">
                            Bidder Name
                        </TableCell>
                        <TableCell className={classes.tableCell} size="small">
                            Bidder Email
                        </TableCell>
                        <TableCell className={classes.tableCell} size="small">
                            Bidder Phone
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(!parcelLogState.logs ||
                        parcelLogState.logs.length === 0) && (
                        <TableRow>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                                colSpan={6}
                            >
                                <Typography
                                    component="div"
                                    variant="body2"
                                    align="center"
                                    color="textSecondary"
                                >
                                    No bid logs to display at this time.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}

                    {parcelLogState.logs &&
                        parcelLogState.logs.length > 0 &&
                        parcelLogState.logs
                            .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                            )
                            .map((parcelBid) =>
                                renderBidRow(parcelBid, topBidId)
                            )}
                </TableBody>
            </Table>

            <Grid container={true} spacing={4}>
                <Grid
                    item={true}
                    xs={2}
                    className={classes.exportButtonWrapper}
                >
                    <ExportBidLogCSVButton
                        parcelId={parcelId}
                        parcelName={parcelName}
                        auctionName={auctionName}
                    />
                </Grid>
                <Grid item={true} xs={10}>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={parcelLogState.logs.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        backIconButtonProps={{
                            "aria-label": "Previous Page"
                        }}
                        nextIconButtonProps={{ "aria-label": "Next Page" }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
        </div>
    );
}

export default withStyles(styles)(ParcelBidLogList);
